<template>
  <div v-if="total > 0" class="pagination-bar">
    <span>Total {{ total }}</span>
    <select class="page-select" :value="pageSize" @change="handleSizeChange(parseInt($event.target.value))">
      <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}/page</option>
    </select>
    <div class="page-nav">
      <div class="page-btn" @click="handlePageChange(Math.max(1, currentPage - 1))" :class="{ disabled: currentPage === 1 }">
        <i class="fa-solid fa-chevron-left"></i>
      </div>
      <template v-for="(pageNum, index) in pageNumbers" :key="index">
        <div 
          v-if="pageNum !== 'ellipsis'"
          class="page-btn"
          :class="{ active: pageNum === currentPage }"
          @click="handlePageChange(pageNum)"
        >
          {{ pageNum }}
        </div>
        <div v-else class="page-ellipsis">...</div>
      </template>
      <div class="page-btn" @click="handlePageChange(Math.min(Math.ceil(total / pageSize), currentPage + 1))" :class="{ disabled: currentPage >= Math.ceil(total / pageSize) }">
        <i class="fa-solid fa-chevron-right"></i>
      </div>
    </div>
    <span>Go to</span>
    <input 
      type="text" 
      :value="currentPage" 
      @change="handlePageChange(parseInt($event.target.value) || 1)"
      class="page-input"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  total: {
    type: Number,
    required: true,
    default: 0
  },
  currentPage: {
    type: Number,
    required: true,
    default: 1
  },
  pageSize: {
    type: Number,
    required: true,
    default: 50
  },
  pageSizes: {
    type: Array,
    default: () => [10, 20, 50, 100]
  }
})

const emit = defineEmits(['page-change', 'size-change'])

// 生成页码列表
const pageNumbers = computed(() => {
  const totalPages = Math.ceil(props.total / props.pageSize)
  const current = props.currentPage
  const pages = []
  
  if (totalPages <= 7) {
    // 如果总页数少于等于7页，显示所有页码
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    // 总是显示第一页
    pages.push(1)
    
    if (current <= 4) {
      // 当前页在前4页，显示前5页
      for (let i = 2; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('ellipsis')
      pages.push(totalPages)
    } else if (current >= totalPages - 3) {
      // 当前页在后4页，显示后5页
      pages.push('ellipsis')
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // 当前页在中间，显示当前页前后各2页
      pages.push('ellipsis')
      for (let i = current - 2; i <= current + 2; i++) {
        pages.push(i)
      }
      pages.push('ellipsis')
      pages.push(totalPages)
    }
  }
  
  return pages
})

const handlePageChange = (page) => {
  const totalPages = Math.ceil(props.total / props.pageSize)
  const validPage = Math.max(1, Math.min(page, totalPages))
  emit('page-change', validPage)
}

const handleSizeChange = (size) => {
  emit('size-change', size)
}
</script>

<style scoped>
.pagination-bar {
  padding: 12px 24px;
  border-top: 1px solid #F3F4F6;
  background: #fff;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #6B7280;
}

.page-select {
  padding: 4px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  color: #1F2937;
  font-size: 12px;
}

.page-nav {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  color: #6B7280;
}

.page-btn:hover:not(.disabled) {
  background: #F3F4F6;
}

.page-btn.active {
  color: #4F46E5;
  font-weight: 600;
}

.page-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-ellipsis {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  font-size: 13px;
}

.page-input {
  width: 40px;
  padding: 4px;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  text-align: center;
}
</style>

